import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Button from '../Button/Button';
import { PWABrowser } from '../../hooks/usePWAInstall';

interface PWAInstallBannerProps {
  onInstall: () => void;
  onDismiss: () => void;
  isIOS?: boolean;
  isAndroid?: boolean;
  isInstallable?: boolean;
  browser?: PWABrowser;
}

const BannerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #3f7a19 0%, #4a8f1f 100%);
  color: white;
  padding: 16px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideUpMobile 0.3s ease-out;

  @keyframes slideUpMobile {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  @keyframes slideUpDesktop {
    from {
      transform: translateX(-50%) translateY(100%);
    }
    to {
      transform: translateX(-50%) translateY(0);
    }
  }

  @media (min-width: 768px) {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    max-width: 500px;
    border-radius: 8px 8px 0 0;
    animation: slideUpDesktop 0.3s ease-out;
  }
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BannerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const AppIcon = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
`;

const TextContent = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
`;

const Description = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  margin-top: -6px;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const StyledButton = styled(Button)`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.2s;

  &.primary {
    background: white;
    color: #3f7a19;
    border: none;

    &:hover {
      background: rgba(255, 255, 255, 0.9);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
  }

  &.secondary {
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.5);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: white;
    }
  }
`;

const getInstructionKey = (
  isIOS: boolean,
  isAndroid: boolean,
  isInstallable: boolean,
  browser: PWABrowser
): string => {
  // Native install prompt available: show generic description with install button
  if (isInstallable) {
    return 'PWA_INSTALL.BANNER.DESCRIPTION';
  }

  if (isIOS) {
    switch (browser) {
      case 'chrome-ios':
        return 'PWA_INSTALL.BANNER.IOS_CHROME_INSTRUCTION';
      case 'firefox-ios':
        return 'PWA_INSTALL.BANNER.IOS_FIREFOX_INSTRUCTION';
      case 'edge-ios':
        return 'PWA_INSTALL.BANNER.IOS_EDGE_INSTRUCTION';
      default:
        return 'PWA_INSTALL.BANNER.IOS_INSTRUCTION';
    }
  }

  if (isAndroid) {
    return browser === 'firefox'
      ? 'PWA_INSTALL.BANNER.ANDROID_FIREFOX_INSTRUCTION'
      : 'PWA_INSTALL.BANNER.ANDROID_INSTRUCTION';
  }

  if (browser === 'safari-desktop') {
    return 'PWA_INSTALL.BANNER.SAFARI_DESKTOP_INSTRUCTION';
  }

  return 'PWA_INSTALL.BANNER.DESCRIPTION';
};

const PWAInstallBanner: React.FC<PWAInstallBannerProps> = ({ 
  onInstall, 
  onDismiss, 
  isIOS = false, 
  isAndroid = false,
  isInstallable = false,
  browser = 'other'
}) => {
  const { t } = useTranslation();
  
  // Determine if we should show manual instructions
  const showManualInstructions = !isInstallable && (isIOS || isAndroid || browser === 'safari-desktop');
  const instructionKey = getInstructionKey(isIOS, isAndroid, isInstallable, browser);

  return (
    <BannerContainer>
      <BannerContent>
        <BannerHeader>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <IconWrapper>
              <AppIcon src="/favicon/android-chrome-192x192.png" alt="Tessa App" />
            </IconWrapper>
            <TextContent>
              <Title>{t('PWA_INSTALL.BANNER.TITLE')}</Title>
              <Description>
                {t(instructionKey)}
              </Description>
            </TextContent>
          </div>
          <CloseButton onClick={onDismiss} aria-label={t('PWA_INSTALL.BANNER.CLOSE')}>
            ×
          </CloseButton>
        </BannerHeader>
        {!showManualInstructions && (
          <ButtonGroup>
            <StyledButton className="primary" onClick={onInstall}>
              {t('PWA_INSTALL.BANNER.INSTALL_BUTTON')}
            </StyledButton>
            <StyledButton className="secondary" onClick={onDismiss}>
              {t('PWA_INSTALL.BANNER.DISMISS_BUTTON')}
            </StyledButton>
          </ButtonGroup>
        )}
        {showManualInstructions && (
          <ButtonGroup>
            <StyledButton className="secondary" onClick={onDismiss}>
              {t('PWA_INSTALL.BANNER.DISMISS_BUTTON')}
            </StyledButton>
          </ButtonGroup>
        )}
      </BannerContent>
    </BannerContainer>
  );
};

export default PWAInstallBanner;
